/** @jsx jsx */
import { Icon } from "@blueprintjs/core";
import { css, jsx } from "@emotion/react";
import { useCallback, useRef, forwardRef } from "react";
import { useEffect, useState } from "react"
import FittingBox from "./FittingBox"
/**
 * Give me all your children, and I will use pagination
 * to display as many as will fit at once and prev/next 
 * buttons to allow navigation to the rest.
 * 
 * Assumes all children are the same size
 */
export default function Paginator({childrenProps,
                                   childRenderFunc: ChildRenderFunc,
                                   ...props}:PaginatorProps){
    const [[pageStart, pageEnd],setPageIndices] = useState([0,1])
    // The number of elements that will fit on a page.
    const [maxNumPerPage,setMaxNumPerPage] = useState(3)
    // Centralizing these numbers because they require a boundary check
    const [[numPerEdgePage,numPerMiddlePage],setNumsPerPage] = useState([2,1])
    const [pageNum,setPageNum] = useState(0)
    const [numPages, setNumPages] = useState(1)
    const firstChildRef = useRef()

    useEffect(()=>{
        setNumsPerPage([
            Math.max(maxNumPerPage-1,0),
            Math.max(maxNumPerPage-2,0)
        ])
    },[maxNumPerPage])

    // Keep page indices up to date
	useEffect(()=>{
        // Only one page
        if (childrenProps.length <= maxNumPerPage) {
            setPageIndices([
                0,
                maxNumPerPage
            ])
            return
        }
        // First page
        if (pageNum === 0) {
            setPageIndices([
                0,
                numPerEdgePage
            ])
            return
        }
        // Last page
        if (pageNum === numPages-1) {
            setPageIndices([
                childrenProps.length-numPerEdgePage,
                childrenProps.length
            ])
            return
        }
        // Middle page
		setPageIndices([
            // Num items per middle page * pageNum + extra item on first page
			numPerMiddlePage*pageNum+1,
			numPerMiddlePage*(pageNum+1)+1
		])
	},[maxNumPerPage,pageNum,numPages,childrenProps.length,numPerMiddlePage,numPerEdgePage])

    // Keep numPages up to date while guaranteeing there is at least 1 page
    useEffect(()=>{
        setNumPages(Math.max(
            // The first/last page show an extra element so subtract 2
            Math.ceil((childrenProps.length-2)/(numPerMiddlePage)),
            1
        ))
    },[numPerMiddlePage,maxNumPerPage,childrenProps.length])

    // Create some booleans that will guide what buttons to show
    const onlyOnePage = maxNumPerPage >= childrenProps.length
    const firstPage = !onlyOnePage && pageNum===0
    const lastPage = pageEnd >=childrenProps.length && !onlyOnePage
    const middlePage = !lastPage && !firstPage && !onlyOnePage

    // Set page num while guaranteeing pageNum is valid
    const nextPage = useCallback(()=>{                                        
        setPageNum(Math.min(pageNum+1,numPages-1))
    },[pageNum,numPages])
    const prevPage = useCallback(()=>{
        setPageNum(Math.max(pageNum-1,0))
    },[pageNum])

    const moreIcon = <Icon  icon='more' 
                            iconSize={ICON_SIZE} 
                            css={IconCss}
                            color={ICON_COLOR}/>

    const nextPageButton = <div onClick={nextPage} 
                                key='nextPageButton'
                                style={{marginTop:"10px"}}
                                css={buttonCss}>
        {moreIcon}
        {" NEXT"}
    </div>
    const prevPageButton = <div onClick={prevPage} 
                                key='prevPageButton'
                                style={{marginBottom:"10px"}}
                                css={buttonCss}>
        {moreIcon}
        {" PREVIOUS"}
    </div>

    const childrenPropsToShow = childrenProps.slice(pageStart,pageEnd)
    const childrenToShow = childrenPropsToShow.map((childProps,i)=>{
        return <ChildRenderFunc {...childProps}
                                // Always assign ref to the first item in list
                                ref={i===0? firstChildRef : undefined}/>
    })

    return (
        <FittingBox firstChildRef={firstChildRef}
                    numFitCallback={setMaxNumPerPage}
                    lowerHeightBy={20}
                    {...props}>
            {/* This is a little messy but it makes the typescript 
            happy and the cleaner solution didn't so its staying */}
            {[(lastPage || middlePage) && prevPageButton]
            .concat(childrenToShow)
            .concat([(firstPage || middlePage) && nextPageButton])}
        </FittingBox>
    )
}

interface PaginatorProps {
    childrenProps: unknown[];
    childRenderFunc: ReturnType<typeof forwardRef>;
    [key:string]:unknown
}

const ICON_SIZE = 14
const ICON_COLOR = '#6699CC'

const IconCss = css`
    border: 2px solid ${ICON_COLOR};
    color: ${ICON_COLOR};
    border-radius: 15px;
    padding: 1px;
    margin-right:4px;
`
const buttonCss = css `
    display: flex;
    align-content: space-around;
    align-items: center;
    cursor: pointer;
`